import { getDisplayPrice } from './priceUtils';

/**
 * What a price group covers, ready to draw (#361): every product in the series with the price we
 * would hold against the group's cap, cheapest first, and whether it is within the price.
 */
const offerPrice = (regular, sale, onSale) => {
    const salePrice = Number(sale);
    if (onSale && Number.isFinite(salePrice) && salePrice > 0) {
        return salePrice;
    }
    const regularPrice = Number(regular);
    return Number.isFinite(regularPrice) && regularPrice > 0 ? regularPrice : null;
};

/**
 * The LOWEST price any retailer lists the product at. An alert fires at whichever retailer is at
 * or under the cap, so that is the number to hold against it. The API's `displayPrice` is the
 * price where the product is in stock right now, which can be the dearer retailer: on 2026-09-20
 * it showed $11,999.98 for a card Best Buy lists at $5,999.99 (#363).
 */
export function currentPriceOf(stock) {
    if (!stock) {
        return null;
    }
    const mc = stock.mcOffer || {};
    const candidates = [
        offerPrice(stock.bestBuyRegularPrice, stock.bestBuySalePrice, stock.bestBuyOnSale),
        offerPrice(mc.regularPrice, mc.salePrice, mc.onSale),
        ...(Array.isArray(stock.retailerOffers) ? stock.retailerOffers : [])
            .map((offer) => offerPrice(offer?.regularPrice, offer?.salePrice, offer?.onSale)),
        offerPrice(stock.displayPrice, null, false),
        getDisplayPrice(stock)
    ].filter((price) => price !== null && price !== undefined && Number.isFinite(Number(price)) && Number(price) > 0);
    return candidates.length ? Math.min(...candidates.map(Number)) : null;
}

// "ASUS - ROG Astral NVIDIA GeForce RTX 5090 32GB GDDR7 PCI Express 5.0 Graphics Card - Black"
// -> "ASUS ROG Astral - Black": inside an RTX 5090 group the rest is the same on every tile.
export function shortProductName(name, series = '') {
    let text = String(name || '');
    if (series) {
        text = text.replace(new RegExp(series.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig'), ' ');
    }
    text = text
        .replace(/(NVIDIA|GeForce|AMD|Radeon)/gi, ' ')      // "GeForceRTX" appears in real listings
        .replace(/\b\d+\s?GB?\b/gi, ' ')                    // 32GB, and MSI/Gigabyte's "32G"
        .replace(/\bGDDR\dX?\b/gi, ' ')
        .replace(/\bPCI Express( Gen)? ?\d(\.\d)?\b/gi, ' ')
        .replace(/\bPCIe ?\d(\.\d)?\b/gi, ' ')
        .replace(/\bGraphics Card\b/gi, ' ')
        .replace(/\s+-\s+/, ' ')       // the dash after the brand
        .replace(/\s{2,}/g, ' ')
        .replace(/\s+-\s*$/, '')
        .trim();
    return text || String(name || '');
}

export function describeGroupProducts(stocks, maxPrice, series = '') {
    const cap = Number(maxPrice);
    const hasCap = Number.isFinite(cap) && cap > 0;
    const products = (Array.isArray(stocks) ? stocks : [])
        .map((stock) => {
            const price = currentPriceOf(stock);
            return {
                sku: stock.sku,
                name: shortProductName(stock.name, series),
                fullName: stock.name,
                image: stock.thumbnailImage || stock.image || '',
                price,
                inStock: Boolean(stock.onlineAvailability || stock.inStoreAvailability),
                withinPrice: hasCap && price !== null && price <= cap
            };
        })
        .sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
    return {
        products,
        total: products.length,
        withinPrice: products.filter((product) => product.withinPrice).length
    };
}
